import { useQuery } from '@tanstack/react-query';
import type { ChartOptions } from 'chart.js';
import type { MouseEvent } from 'react';
import { Bar } from 'react-chartjs-2';
import { ErrorBoundary } from 'react-error-boundary';

import { CenterFlex, Flex } from '@/components/base';
import Transition from '@/components/Common/Transition';
import { MainText } from '@/components/Typography/NamedTypo';
import queryKeys from '@/configs/react-query-keys';
import { CollapseState } from '@/features/AskSyncly/types/constants';
import type { FeedbackBlockListModalOptions } from '@/features/Feedback/components/FeedbackListModal/types';
import { useSearchDetailContext } from '@/features/GlobalSearch/context/SearchDetailContext';
import useGlobalSearchModalAction from '@/features/GlobalSearch/hooks/useGlobalSearchModalAction';
import { useScoreFilterMap } from '@/features/StructuredFilter/configs/useScoreFilterMap';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useColor from '@/hooks/domain/useColor';
import useModalState from '@/hooks/pure/useModalState';
import service from '@/services/index';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { GPT_DOMAIN } from '@/types/gptConst';

import SearchContentStyles from '../SearchContentStyles';
import SearchDrilldownMenu from '../SearchDrilldownMenu';

type Props = {
  field: string;
  title: string;
};

const { ContentBox, ContentHeader, DownloadButton } = SearchContentStyles;

const ScoreDistChartContainer = (props: Props) => {
  const filterMap = useScoreFilterMap();
  const { addToFilterSingleValue } = useGlobalSearchModalAction();
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const { searchResult, filterQueryFormats } = useSearchDetailContext();
  const color = useColor();
  const { data, isLoading } = useQuery({
    queryFn: () =>
      service.globalSearch.fetchFeedbackFieldDist(
        searchResult.alias,
        props.field,
      ),
    queryKey: [
      queryKeys.search.SEARCH_DETAIL,
      queryKeys.search.SEARCH_RESULT,
      props.field,
      searchResult.alias,
    ],
  });

  const { status, open, close, pos, setPos, modalState, setModalState } =
    useModalState<SelectedChartBar>(null);

  const chartData = {
    labels: data?.buckets.map((bucket) => bucket.bucket),
    datasets: [
      {
        label: 'Score',
        data: data?.buckets.map((bucket) => bucket.count),
        backgroundColor: [
          color.gray400,
          color.gray400,
          color.gray400,
          color.gray500,
          color.gray500,
          color.gray500,
          color.gray500,
          color.gray600,
          color.gray600,
          color.gray600,
        ],
      },
    ],
  };

  const chartOptions: ChartOptions<'bar'> = {
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
      },
    },
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: false,
      },
    },
    onClick: (e: any) => {
      const el = e.chart.tooltip.dataPoints[0];

      if (!el) return;
      const val: SelectedChartBar = {
        dataIndex: el.dataIndex,
        datasetIndex: el.datasetIndex,
      };

      setPos({
        top: e.native.clientY,
        left: e.native.clientX,
      });
      setModalState(val);
      open();
    },
  };

  const onAddToFilterHandler = async (e: MouseEvent) => {
    e.stopPropagation();
    close();

    if (!modalState) return;

    const value = chartData.labels?.[modalState?.dataIndex];

    if (!value) return;

    await addToFilterSingleValue(searchResult, filterQueryFormats, {
      segment: {
        value: Number(value),
        name: value.toString(),
      },
      columnKey: props.field,
    });
  };

  const onViewFeedbackListHandler = async (
    e: MouseEvent,
    initialOpen?: boolean,
  ) => {
    e.stopPropagation();
    close();
    if (!modalState) return;
    const value = chartData.labels?.[modalState?.dataIndex];
    if (!value) return;

    const filterPayload = {
      [props.field]: structuredFilterUtils.buildSingleValue(value),
    };
    setNewModal<FeedbackBlockListModalOptions>({
      alias: searchResult.alias,
      type: DETAIL_MODAL_TYPE.FEEDBACK_BLOCK_LIST,
      idx: 0,
      keys: [props.field, value.toString()],
      options: {
        queryKeys: [props.field, value.toString()],
        title: 'Feedback list',
        subTitle: `${filterMap[props.field].columnName} : ${value}`,
        domain: 'global-search',
        fetcher: async (
          sortStatus: NotNullableSortStatusT,
          pageParam?: null | string,
        ) => {
          const res = await service.globalSearch.fetchSearchFeedbackList(
            searchResult.alias,
            {
              query: {
                cursor: pageParam,
                sortby: sortStatus.sortBy,
                order: sortStatus.orderStatus,
              },
              payload: filterPayload,
            },
          );
          const PAGE_SIZE = 50;
          return {
            count: res.count,
            list: res.feedbacks,
            paginate_token: res.feedbacks[PAGE_SIZE]?.alias,
          };
        },
        exportFetcher: async (reason?: string, password?: string) => {
          await service.globalSearch.exportSearchFeedbackList({
            alias: searchResult.alias,
            filterPayload: filterPayload,
            reason: reason,
            password: password,
          });
        },
        socketInfo: {
          domain: GPT_DOMAIN.GLOBAL_SEARCH,
          search: {
            alias: searchResult.alias,
            filterPayload: filterPayload,
          },
          initialState: initialOpen ? CollapseState.EXPANDED : undefined,
        },
      },
    });
  };

  return (
    <ContentBox padding="24px" g={24}>
      <Flex j="space-between" a="center">
        <ContentHeader>{props.title}</ContentHeader>
        <DownloadButton onClick={() => {}} />
      </Flex>
      <ErrorBoundary
        fallback={
          <CenterFlex style={{ height: 250 }}>
            <MainText dim>Error occurred</MainText>
          </CenterFlex>
        }
      >
        <Transition
          status={!isLoading}
          style={{ height: 200, cursor: 'pointer' }}
        >
          <Bar data={chartData} options={chartOptions} />
        </Transition>
      </ErrorBoundary>
      <SearchDrilldownMenu
        pos={{
          top: pos?.top as number,
          left: pos?.left as number,
        }}
        status={status}
        close={close}
        onViewFeedbackListClick={onViewFeedbackListHandler}
        onAddToFilterClick={onAddToFilterHandler}
        onAskSynclyAIClick={(e) => onViewFeedbackListHandler(e, true)}
      />
    </ContentBox>
  );
};

export default ScoreDistChartContainer;
